// Claude Code Chat Controller with Ruby SDK Support
import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["messages", "input", "typing", "messagesContainer"]
  static values = { sessionId: String }
  
  connect() {
    console.log('Claude Code Chat controller connected with sessionId:', this.sessionIdValue)
    this.setupChannel()
    this.scrollToBottom()
    this.currentThinkingElement = null
  }
  
  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }
  
  setupChannel() {
    console.log('Setting up ActionCable channel for Ruby SDK...')
    
    this.channel = createConsumer().subscriptions.create(
      { 
        channel: "ClaudeCodeChannel", 
        session_id: this.sessionIdValue 
      },
      {
        connected: () => {
          console.log("Connected to ClaudeCodeChannel (Ruby SDK)")
          this.testConnection()
        },
        disconnected: () => {
          console.log("Disconnected from ClaudeCodeChannel")
        },
        received: (data) => this.handleReceived(data)
      }
    )
  }
  
  testConnection() {
    // Test the Ruby SDK connection
    this.channel.perform('test_connection')
  }
  
  handleReceived(data) {
    console.log('Received (Ruby controller):', data)
    
    // Guard against malformed data
    if (!data || typeof data !== 'object') {
      console.error('Received malformed data:', data)
      return
    }
    
    // Skip broadcasts without a type
    if (!data.type) {
      console.warn('Received data without type:', data)
      return
    }
    
    switch(data.type) {
      case 'connected':
        console.log('Session connected with SDK version:', data.sdk_version)
        break
        
      case 'test_result':
        if (data.success) {
          console.log('Ruby SDK test successful:', data.response)
        } else {
          console.error('Ruby SDK test failed:', data.error)
          this.showError(`SDK Error: ${data.error}`)
        }
        break
        
      case 'thinking':
        this.showThinking(data.content)
        break
        
      case 'assistant_message':
        this.hideThinking()
        this.appendMessage('assistant', data.content)
        break
        
      case 'tool_use':
        this.hideThinking()
        this.showToolUse(data.content, data.tool_calls)
        break
        
      case 'tool_result':
        this.showToolResult(data.tool_results)
        break
        
      case 'system_message':
        // Only show system messages with valid content
        if (data.content && data.content.toString().trim() !== '' && 
            data.content !== 'undefined' && data.content !== 'null' && data.content !== 'undefined: null') {
          this.showSystemMessage(data.content)
        } else {
          console.log('Skipping system_message with empty/invalid content:', data)
        }
        break
        
      case 'result_message':
        this.showConversationComplete(data.metadata)
        // Also re-render the last message to ensure proper formatting
        setTimeout(() => {
          this.reRenderLastMessage()
        }, 500)
        break
        
      case 'session_state':
        this.updateSessionState(data.state)
        break
        
      case 'error':
        this.hideThinking()
        this.showError(data.message)
        break
        
      case 'message_queued':
        // Message was queued for processing
        break
        
      case 'context_update':
        this.updateContextDisplay(data.files)
        break
        
      default:
        console.log('Unknown message type:', data.type)
    }
  }
  
  showThinking(content) {
    // Remove any existing thinking indicator
    this.hideThinking()
    
    // Create new thinking element
    this.currentThinkingElement = document.createElement('div')
    this.currentThinkingElement.className = 'chat chat-start mb-2 thinking-indicator'
    this.currentThinkingElement.innerHTML = `
      <div class="chat-bubble bg-base-200 italic opacity-70">
        <div class="flex items-center gap-2">
          <div class="loading loading-dots loading-xs"></div>
          <span class="thinking-content">${this.escapeHtml(content)}</span>
        </div>
      </div>
    `
    
    this.messagesTarget.appendChild(this.currentThinkingElement)
    this.scrollToBottom()
  }
  
  hideThinking() {
    if (this.currentThinkingElement) {
      // Fade out the thinking indicator
      this.currentThinkingElement.style.transition = 'opacity 0.3s ease'
      this.currentThinkingElement.style.opacity = '0'
      
      setTimeout(() => {
        if (this.currentThinkingElement && this.currentThinkingElement.parentNode) {
          this.currentThinkingElement.remove()
        }
        this.currentThinkingElement = null
      }, 300)
    }
  }
  
  showToolUse(content, toolCalls) {
    // Show the message content if any
    if (content && content.trim()) {
      this.appendMessage('assistant', content)
    }
    
    // Show each tool call
    toolCalls.forEach(toolCall => {
      const toolHtml = `
        <div class="alert alert-info mb-2 tool-use">
          <div class="flex items-center gap-2">
            <i class="fas fa-cog"></i>
            <div>
              <div class="font-semibold">Using tool: ${toolCall.name}</div>
              <div class="text-sm opacity-70">
                ${this.formatToolInput(toolCall.input)}
              </div>
            </div>
          </div>
        </div>
      `
      this.messagesTarget.insertAdjacentHTML('beforeend', toolHtml)
    })
    
    this.scrollToBottom()
  }
  
  showToolResult(toolResults) {
    toolResults.forEach(result => {
      const resultClass = result.is_error ? 'alert-error' : 'alert-success'
      const icon = result.is_error ? 'fas fa-exclamation-triangle' : 'fas fa-check-circle'
      
      const resultHtml = `
        <div class="alert ${resultClass} mb-2 tool-result">
          <div class="flex items-center gap-2">
            <i class="${icon}"></i>
            <div class="font-mono text-sm">
              ${this.escapeHtml(result.content)}
            </div>
          </div>
        </div>
      `
      this.messagesTarget.insertAdjacentHTML('beforeend', resultHtml)
    })
    
    this.scrollToBottom()
  }
  
  showSystemMessage(content) {
    // Skip system messages with empty or problematic content
    if (!content || content === 'undefined' || content === 'null' || content === 'undefined: null' || content === null || content.toString().trim() === '') {
      console.log('Skipping system message with empty/invalid content:', { content })
      return
    }
    
    // Debug logging for system messages
    console.log('Appending system message (Ruby controller):', content)
    
    // Convert JSON strings to markdown format
    const formattedContent = this.formatSystemMessageContent(content)
    
    const systemHtml = `
      <div class="alert alert-info mb-2 system-message">
        <div class="flex items-center gap-2">
          <i class="fas fa-info-circle"></i>
          <div class="prose prose-sm max-w-none">${formattedContent}</div>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', systemHtml)
    this.scrollToBottom()
  }
  
  showConversationComplete(metadata) {
    if (metadata && metadata.usage) {
      const usage = metadata.usage
      const usageHtml = `
        <div class="alert alert-success mb-2 conversation-complete">
          <div>
            <div class="font-semibold">Conversation Complete</div>
            <div class="text-sm opacity-70">
              Tokens used: ${usage.total_tokens || 'N/A'} 
              ${metadata.cost ? `• Cost: $${metadata.cost}` : ''}
            </div>
          </div>
        </div>
      `
      this.messagesTarget.insertAdjacentHTML('beforeend', usageHtml)
    }
    this.scrollToBottom()
  }
  
  formatToolInput(input) {
    if (typeof input === 'object') {
      // Show key information for common tools
      if (input.path) return `File: ${input.path}`
      if (input.file_path) return `File: ${input.file_path}`
      if (input.command) return `Command: ${input.command}`
      return JSON.stringify(input)
    }
    return String(input)
  }
  
  updateSessionState(state) {
    // Update UI to reflect session state
    const stateElement = document.querySelector('[data-session-state]')
    if (stateElement) {
      stateElement.textContent = state
      stateElement.className = `badge badge-${this.getStateColor(state)}`
    }
  }
  
  getStateColor(state) {
    switch(state) {
      case 'active': return 'success'
      case 'processing': return 'warning'
      case 'error': return 'error'
      case 'idle': return 'neutral'
      default: return 'ghost'
    }
  }
  
  updateContextDisplay(files) {
    // Update any context file display
    const contextElement = document.querySelector('[data-context-files]')
    if (contextElement && files) {
      contextElement.textContent = `${files.length} files in context`
    }
  }
  
  sendMessage(event) {
    event.preventDefault()
    
    const message = this.inputTarget.value.trim()
    if (!message) return
    
    // Check if channel is connected
    if (!this.channel) {
      console.error('Channel not connected')
      this.showError('Connection lost. Please refresh the page.')
      return
    }
    
    // Show user message immediately
    this.appendMessage('user', message)
    
    // Send to server
    console.log('Sending message to Ruby SDK:', message)
    this.channel.perform('execute', { 
      prompt: message,
      context_mode: 'smart', // TODO: make configurable
      auto_apply: false      // TODO: make configurable
    })
    
    // Clear input and show typing
    this.inputTarget.value = ''
    this.showTyping()
  }
  
  appendMessage(role, content) {
    const messageHtml = `
      <div class="chat chat-${role === 'user' ? 'end' : 'start'} mb-2">
        <div class="chat-bubble ${role === 'user' ? 'chat-bubble-primary' : ''}">
          ${this.escapeHtml(content)}
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }
  
  showTyping() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.remove('hidden')
      this.scrollToBottom()
    }
  }
  
  hideTyping() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.add('hidden')
    }
  }
  
  showError(message) {
    const errorHtml = `
      <div class="alert alert-error mb-2">
        <i class="fas fa-exclamation-circle"></i>
        <span>${this.escapeHtml(message)}</span>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', errorHtml)
    this.scrollToBottom()
  }
  
  showSuccess(message) {
    const successHtml = `
      <div class="alert alert-success mb-2">
        <i class="fas fa-check-circle"></i>
        <span>${this.escapeHtml(message)}</span>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', successHtml)
    this.scrollToBottom()
  }
  
  scrollToBottom() {
    if (this.hasMessagesContainerTarget) {
      this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight
    }
  }
  
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  formatSystemMessageContent(content) {
    if (!content || typeof content !== 'string') {
      return this.escapeHtml(String(content || ''))
    }
    
    // Clean up the content string
    const cleanContent = content.toString().trim()
    
    // Check if the content looks like JSON
    if (this.isJsonString(cleanContent)) {
      try {
        const parsed = JSON.parse(cleanContent)
        return this.convertJsonToMarkdown(parsed)
      } catch (e) {
        console.log('Failed to parse JSON in system message:', e)
        // Fall back to regular formatting
        return this.escapeHtml(cleanContent)
      }
    }
    
    // Check if content contains JSON-like structures within text
    const jsonMatches = cleanContent.match(/\{[\s\S]*?\}/g)
    if (jsonMatches && jsonMatches.length > 0) {
      let formattedContent = cleanContent
      
      jsonMatches.forEach(match => {
        try {
          const parsed = JSON.parse(match)
          const markdownJson = this.convertJsonToMarkdown(parsed)
          formattedContent = formattedContent.replace(match, markdownJson)
        } catch (e) {
          // Keep original if parsing fails
        }
      })
      
      return formattedContent
    }
    
    // No JSON detected, return escaped HTML
    return this.escapeHtml(cleanContent)
  }
  
  isJsonString(str) {
    const trimmed = str.trim()
    return (trimmed.startsWith('{') && trimmed.endsWith('}')) || 
           (trimmed.startsWith('[') && trimmed.endsWith(']'))
  }
  
  convertJsonToMarkdown(obj) {
    if (typeof obj === 'string') {
      return this.escapeHtml(obj)
    }
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return '`[]` (empty array)'
      }
      
      let markdown = '**Array:**\n\n'
      obj.forEach((item, index) => {
        markdown += `${index + 1}. ${this.convertJsonToMarkdown(item)}\n`
      })
      return markdown
    }
    
    if (typeof obj === 'object' && obj !== null) {
      if (Object.keys(obj).length === 0) {
        return '`{}` (empty object)'
      }
      
      let markdown = '**Object:**\n\n'
      Object.entries(obj).forEach(([key, value]) => {
        markdown += `- **${this.escapeHtml(key)}**: `
        
        if (typeof value === 'string' && (value.includes('\n') || value.length > 60)) {
          // Multi-line or long strings get code blocks
          markdown += '\n\n```\n' + value + '\n```\n\n'
        } else if (typeof value === 'object' && value !== null) {
          // Nested objects get indented JSON
          markdown += '\n\n```json\n' + JSON.stringify(value, null, 2) + '\n```\n\n'
        } else {
          // Simple values inline
          markdown += `\`${this.escapeHtml(String(value))}\`\n`
        }
      })
      return markdown
    }
    
    // Primitive values
    return `\`${this.escapeHtml(String(obj))}\``
  }
  
  reRenderLastMessage() {
    // Find the last message in the messages container
    const messagesList = this.messagesTarget
    const messageElements = messagesList.querySelectorAll('[data-message-id]')
    
    if (messageElements.length === 0) {
      console.log('No messages found to re-render')
      return
    }
    
    // Get the last message element
    const lastMessageElement = messageElements[messageElements.length - 1]
    const messageId = lastMessageElement.getAttribute('data-message-id')
    
    if (!messageId) {
      console.log('Last message has no message ID')
      return
    }
    
    console.log('Re-rendering message (Ruby controller):', messageId)
    
    // Fetch the properly rendered message from the server
    const sessionId = this.sessionIdValue
    const url = `/claude_code/sessions/${sessionId}/render_message?message_id=${encodeURIComponent(messageId)}`
    
    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.html && data.message_id === messageId) {
        // Replace the message element with the properly rendered version
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.html
        const newMessageElement = tempDiv.firstElementChild
        
        if (newMessageElement) {
          lastMessageElement.replaceWith(newMessageElement)
          this.scrollToBottom()
          console.log('Message re-rendered successfully (Ruby controller)')
        } else {
          console.error('Failed to create new message element from HTML')
        }
      } else {
        console.error('Invalid response data:', data)
      }
    })
    .catch(error => {
      console.error('Failed to re-render message (Ruby controller):', error)
      // Don't fallback to reload in the ruby controller since showConversationComplete already handles the UI
      console.log('Skipping fallback reload in Ruby controller')
    })
  }
  
  // Handle Enter key to send message
  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }
}